import React, { useContext, useEffect } from 'react';
import PropTypes, {
  bool,
  string,
  arrayOf,
  objectOf,
  shape
} from 'prop-types';
import classNames from 'classnames';

import {
  extend,
  params,
  string as stringType,
  useDataModel,
  arrayOf as arrayOfType,
  bool as boolType,
  QueryContext,
  QueryProvider
} from '@thd-nucleus/data-sources';

import { ExperienceContext } from '@thd-nucleus/experience-context';
import { SalientPoints } from '@thd-olt-component-react/salient-points';
import { Col } from '@thd-olt-component-react/core-ui';

import { ProductModel } from '../../models/product';
import { Header } from '../header/Header';
import { PackagePodList } from '../packagePodList/PackagePodList';
import { Summary } from '../summary/Summary';
import { OrderAPackageProvider } from '../../OrderAPackageContext';
import { DataModelWrapper } from '../dataModelWrapper/DataModelWrapper';
import { FulfillmentCard } from '../subcomponents/fulfillmentCard/FulfillmentCard';
import {
  isCategoryGeneralMerch,
  mergePackageAndFulfillmentAndHighlightsAndQuantityLimits,
} from '../fulfillmentUtils';
import { createDefaultVariables } from '../utils';
import './orderAPackage.scss';

export const OrderAPackage = ({
  data: packageData,
  loading: packageLoading,
  error: packageError,
  packageId,
  storeId
}) => {
  useEffect(() => { LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('order-a-package.ready'); }, []);
  const { channel } = useContext(ExperienceContext);
  const { defaultVariables } = useContext(QueryContext) || {};

  const isMobile = channel === 'mobile';
  const isMobileOrTablet = isMobile || channel === 'tablet';

  const mainHeaderClassNames = classNames(
    'order-a-package__main-header',
    { 'order-a-package__main-header--landscape-enabled': isMobile }
  );
  const itemIds = packageData?.packages?.[0]?.products?.map(({ itemId }) => itemId) || [];
  const productLength = itemIds.length;
  const packageCategory = packageData?.packages?.[0]?.metadata?.packageCategory;
  const isGMPackage = isCategoryGeneralMerch(packageCategory);
  const newDefaultVariables = createDefaultVariables(storeId, defaultVariables);

  const opts = {
    variables: {
      storeId,
      itemIds,
    },
    ssr: false,
    skip: !productLength || !isGMPackage
  };
  const { data: productsData, loading: isProductsLoading, error: productsError } =
    useDataModel('products', opts);

  const productsQueryOptions = ({ options }) => {
    return {
      ...options,
      fetchPolicy: 'no-cache'
    };
  };

  const mountedFn = () => {
    return storeId !== '8119';
  };
  if (!productLength) return null;

  let fulfillmentProducts = packageData;
  if (!productsError && !isProductsLoading && productsData) {
    fulfillmentProducts = mergePackageAndFulfillmentAndHighlightsAndQuantityLimits(packageData, productsData);
  }

  return (
    <article className="order-a-package" data-testid="order-a-package" data-component="OrderAPackage">
      <OrderAPackageProvider
        data={fulfillmentProducts}
        loading={packageLoading}
        error={packageError}
        packageId={packageId}
        isCustomerIdentified={defaultVariables?.current?.isBrandPricingPolicyCompliant || false}
      >
        <QueryProvider
          dataSource="packages"
          mounted={mountedFn}
          defaultVariables={newDefaultVariables}
          cacheKey="order-a-package"
        >
          <DataModelWrapper>
            <Col nopadding={isMobileOrTablet}>
              <Col nopadding={isMobileOrTablet} flatten={!isMobileOrTablet} className={mainHeaderClassNames}>
                <Header card />
              </Col>
              <Col md="8" lg="8" nopadding={isMobileOrTablet} flatten={!isMobileOrTablet}>
                <Col nopadding>
                  <PackagePodList storeId={storeId} />
                </Col>
              </Col>
              <Col nopadding={isMobileOrTablet} md="4" lg="4" className="order-a-package__summary">
                <QueryProvider
                  dataSource="catalog"
                  queryOptions={productsQueryOptions}
                  cacheKey="order-a-package-summary"
                >
                  <Summary
                    card
                  />
                </QueryProvider>
                { isGMPackage && (
                  <Col className="fulfillment-card card">
                    <FulfillmentCard />
                  </Col>
                )}
              </Col>
            </Col>
            { isMobile && (
              <Col
                flatten={isMobileOrTablet}
                fallback="4"
                xs="4"
                sm="0"
                md="0"
                lg="0"
                className="order-a-package__landscape-header"
              >
                <Header card landscape />
              </Col>
            )}
          </DataModelWrapper>
        </QueryProvider>
      </OrderAPackageProvider>
    </article>
  );
};

OrderAPackage.propTypes = {
  data: shape({
    /* eslint-disable-next-line react/forbid-prop-types */
    packages: arrayOf(PropTypes.object)
  }).isRequired,
  storeId: string.isRequired,
  loading: bool.isRequired,
  /* eslint-disable-next-line react/forbid-prop-types */
  error: objectOf(PropTypes.any),
  packageId: string.isRequired,
};

OrderAPackage.defaultProps = {
  error: null,
};

OrderAPackage.displayName = 'OrderAPackage';

OrderAPackage.dataModel = extend({
  products: params({
    itemIds: arrayOfType(
      stringType().isRequired()
    ).isRequired(),
  }).arrayOf(ProductModel)
}, SalientPoints.dataModel);
